import {
  PortalArticleBlock,
  PortalArticleTopicBlock,
  PortalArticleTopicItem,
  PortalArtigoModel,
  PortalEditorSectionChange,
  PortalEditorSectionTarget,
} from '../../../models/PortalArtigoModel'
import { normalizePortalArtigoForSave } from './artigo-normalize'

export interface PortalSectionSaveControlProps {
  disabled: boolean
  dirty: boolean
  loading: boolean
  error?: string
  onSave: () => void
}

export function getPortalEditorSectionKey(target: PortalEditorSectionTarget) {
  if (target.scope === 'rootBlock') return `root:${target.blockType}`
  if (target.scope === 'topicItem') return `topic:${target.topicId}:${target.itemId}`
  return `field:${target.field}`
}

export function getPortalEditorSectionValue(
  artigo: PortalArtigoModel,
  target: PortalEditorSectionTarget
): unknown {
  if (target.scope === 'articleField') return artigo[target.field]
  if (target.scope === 'rootBlock') {
    return (artigo.conteudo || []).find((block) => block.type === target.blockType)
  }
  return getTopic(artigo.conteudo, target.topicId)?.items.find(
    (item) => item.id === target.itemId
  )
}

export function getNormalizedPortalEditorSectionValue(
  artigo: PortalArtigoModel,
  target: PortalEditorSectionTarget
) {
  return getPortalEditorSectionValue(normalizePortalArtigoForSave(artigo), target)
}

export function portalEditorSectionExists(
  artigo: PortalArtigoModel,
  target: PortalEditorSectionTarget
) {
  if (target.scope === 'articleField') {
    const value = getPortalEditorSectionValue(artigo, target)
    return value !== undefined && value !== null
  }
  return getPortalEditorSectionValue(artigo, target) !== undefined
}

export function isPortalEditorSectionDirty(
  artigo: PortalArtigoModel,
  baseline: PortalArtigoModel,
  target: PortalEditorSectionTarget
) {
  return !sameValue(
    getNormalizedPortalEditorSectionValue(artigo, target),
    getNormalizedPortalEditorSectionValue(baseline, target)
  )
}

export function applyPortalEditorSectionChanges(
  artigo: PortalArtigoModel,
  changes: PortalEditorSectionChange[]
) {
  return changes.reduce((current, change) => applyChange(current, change), artigo)
}

export function mergeSavedChangesIntoDraft(
  currentDraft: PortalArtigoModel,
  submittedDraft: PortalArtigoModel,
  changes: PortalEditorSectionChange[]
) {
  return changes.reduce((draft, change) => {
    const currentValue = getPortalEditorSectionValue(draft, change.target)
    const submittedValue = getPortalEditorSectionValue(submittedDraft, change.target)
    const mergedValue = mergePersistedValue(currentValue, submittedValue, change.value)
    return applyChange(draft, { ...change, value: mergedValue })
  }, currentDraft)
}

function applyChange(artigo: PortalArtigoModel, change: PortalEditorSectionChange) {
  const { target, value } = change
  if (target.scope === 'articleField') {
    return { ...artigo, [target.field]: clone(value) }
  }
  if (target.scope === 'rootBlock') {
    return {
      ...artigo,
      conteudo: artigo.conteudo.map((block) =>
        block.type === target.blockType ? (clone(value) as PortalArticleBlock) : block
      ),
    }
  }
  return {
    ...artigo,
    conteudo: artigo.conteudo.map((block) => {
      if (block.type !== 'topic' || block.id !== target.topicId) return block
      return {
        ...block,
        items: block.items.map((item) =>
          item.id === target.itemId ? (clone(value) as PortalArticleTopicItem) : item
        ),
      }
    }),
  }
}

function mergePersistedValue(current: unknown, submitted: unknown, persisted: unknown): unknown {
  if (sameValue(current, submitted)) return clone(persisted)
  if (Array.isArray(current) && Array.isArray(submitted) && Array.isArray(persisted)) {
    return current.map((value, index) =>
      index < submitted.length && index < persisted.length
        ? mergePersistedValue(value, submitted[index], persisted[index])
        : value
    )
  }
  if (isRecord(current) && isRecord(submitted) && isRecord(persisted)) {
    return Object.keys(persisted).reduce<Record<string, unknown>>(
      (merged, key) => {
        merged[key] = mergePersistedValue(current[key], submitted[key], persisted[key])
        return merged
      },
      { ...current }
    )
  }
  return current
}

function getTopic(blocks: PortalArticleBlock[], topicId: string) {
  return blocks.find(
    (block): block is PortalArticleTopicBlock => block.type === 'topic' && block.id === topicId
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function sameValue(first: unknown, second: unknown) {
  return JSON.stringify(first) === JSON.stringify(second)
}

function clone<T>(value: T): T {
  if (value === undefined) return value
  return JSON.parse(JSON.stringify(value)) as T
}
